// Product observations only. Payment and task facts are recorded by Gateway.
export class AnalyticsQueue {
  constructor({
    endpoint,
    consent,
    fetcher = (...args) => globalThis.fetch(...args),
    storage,
    key = 'unicus:analytics:v1',
  }) {
    Object.assign(this, { endpoint, consent, fetcher, storage, key })
    this.pending = []
    this.rejected = []
    this.dropped = 0
    this.failures = 0
    this.nextAttempt = 0
    if (consent()) {
      try {
        this.pending = JSON.parse(storage?.getItem(key) || '[]')
      } catch {
        this.pending = []
      }
    }
    if (!Array.isArray(this.pending)) this.pending = []
    this.trim()
  }
  trim() {
    this.pending = this.pending.filter(
      (e) => typeof e?.event_id === 'string' && Date.now() - Date.parse(e.occurred_at) < 86_400_000,
    )
    while (this.pending.length > 1000) {
      const index = this.pending.findIndex((e) =>
        ['page_view', 'module_view', 'world_card_view'].includes(e.event_name),
      )
      this.pending.splice(index < 0 ? 0 : index, 1)
      this.dropped++
    }
  }
  save() {
    try {
      if (this.consent()) this.storage?.setItem(this.key, JSON.stringify(this.pending))
      else this.storage?.removeItem(this.key)
    } catch {
      /* Memory queue remains available when browser storage is full. */
    }
  }
  clear() {
    this.pending = []
    this.rejected = []
    this.save()
  }
  add(event) {
    if (!this.consent()) {
      this.clear()
      return
    }
    this.pending.push(event)
    this.trim()
    this.save()
  }
  async flush() {
    if (!this.consent()) {
      this.clear()
      return
    }
    if (this.busy || Date.now() < this.nextAttempt) return
    this.trim()
    const events = []
    for (const event of this.pending.slice(0, 20)) {
      const next = { ...event, sent_at: new Date().toISOString() }
      if (new TextEncoder().encode(JSON.stringify({ events: [next] })).length > 60_000) {
        this.pending = this.pending.filter((item) => item.event_id !== event.event_id)
        this.rejected.push({ event_id: event.event_id, field: 'event_size' })
        this.rejected = this.rejected.slice(-50)
        this.save()
        continue
      }
      if (new TextEncoder().encode(JSON.stringify({ events: [...events, next] })).length > 60_000)
        break
      events.push(next)
    }
    if (!events.length) return
    this.busy = true
    try {
      const response = await this.fetcher(this.endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'omit',
        keepalive: true,
        body: JSON.stringify({ events }),
        signal: AbortSignal.timeout(8000),
      })
      if (!response.ok) {
        const retry = response.headers?.get('retry-after')
        if (retry)
          this.nextAttempt =
            Date.now() + Math.max(0, Number(retry) * 1000 || Date.parse(retry) - Date.now())
        throw Error('ANALYTICS_DELIVERY_FAILED')
      }
      const { results } = await response.json()
      if (!this.consent()) {
        this.clear()
        return
      }
      const sent = new Set(events.map((e) => e.event_id))
      const resolved = new Set()
      for (const result of results ?? []) {
        if (!sent.has(result.event_id)) continue
        if (['accepted', 'duplicate', 'rejected'].includes(result.status))
          resolved.add(result.event_id)
        if (result.status === 'rejected')
          this.rejected.push({ event_id: result.event_id, field: result.field })
      }
      this.rejected = this.rejected.slice(-50)
      this.pending = this.pending.filter((e) => !resolved.has(e.event_id))
      this.save()
      this.failures = 0
    } catch {
      this.nextAttempt = Math.max(
        this.nextAttempt,
        Date.now() +
          [2000, 5000, 15000, 60000][Math.min(this.failures++, 3)] +
          Math.random() * 1000,
      )
    } finally {
      this.busy = false
    }
  }
}

export function observeExposure(
  element,
  callback,
  {
    document: doc = document,
    window: win = window,
    schedule = setTimeout,
    cancel = clearTimeout,
    Mutation = MutationObserver,
    visibleWhen = () => true,
  } = {},
) {
  let timer
  let done = false
  const visible = () => {
    if (
      done ||
      !visibleWhen() ||
      doc.visibilityState !== 'visible' ||
      !element.isConnected ||
      !element.getClientRects().length
    )
      return false
    const r = element.getBoundingClientRect()
    const area =
      Math.max(0, Math.min(r.right, win.innerWidth) - Math.max(r.left, 0)) *
      Math.max(0, Math.min(r.bottom, win.innerHeight) - Math.max(r.top, 0))
    const denominator = Math.min(r.width * r.height, win.innerWidth * win.innerHeight)
    return denominator > 0 && area / denominator >= 0.5
  }
  const check = () => {
    if (!visible()) {
      if (timer !== undefined) cancel(timer)
      timer = undefined
      return
    }
    if (timer === undefined)
      timer = schedule(() => {
        timer = undefined
        if (visible()) {
          done = true
          callback()
          cleanup()
        }
      }, 1000)
  }
  const cleanup = () => {
    done = true
    if (timer !== undefined) cancel(timer)
    win.removeEventListener('scroll', check, true)
    win.removeEventListener('resize', check)
    doc.removeEventListener('visibilitychange', check)
    observer.disconnect()
  }
  const observer = new Mutation(check)
  observer.observe(doc.body, {
    attributes: true,
    subtree: true,
    childList: true,
    attributeFilter: ['hidden', 'class', 'style', 'open', 'src'],
  })
  win.addEventListener('scroll', check, true)
  win.addEventListener('resize', check)
  doc.addEventListener('visibilitychange', check)
  check()
  return cleanup
}

export function createTracker(endpoint, environment = 'staging') {
  const consent = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true
  let storage
  try {
    storage = window.localStorage
  } catch {
    /* Tracking still works in memory after consent. */
  }
  const key = `unicus:analytics:v1:${new URL(endpoint).origin}`
  let tabKey
  try {
    tabKey = sessionStorage.getItem('unicus:analytics:tab')
    if (!tabKey) {
      tabKey = crypto.randomUUID()
      sessionStorage.setItem('unicus:analytics:tab', tabKey)
    }
  } catch {
    tabKey = crypto.randomUUID()
  }
  const queue = new AnalyticsQueue({ endpoint, consent, storage, key: `${key}:${tabKey}` })
  const page = crypto.randomUUID()
  let identity
  const acquisition = {}
  const parameters = new URLSearchParams(location.search)
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
    const value = parameters.get(key)
    acquisition[key] = value && /^[A-Za-z0-9_.~-]{1,80}$/.test(value) ? value : null
  }
  acquisition.paid_search_click = ['gclid', 'gbraid', 'wbraid', 'msclkid'].some((name) => parameters.has(name))
  let referrerHost = null
  try {
    referrerHost = document.referrer ? new URL(document.referrer).host : null
  } catch {
    /* Ignore malformed referrers. */
  }
  const external = referrerHost && referrerHost !== location.host
  let attributed = false
  const exposures = new Map()
  const readIdentity = () => {
    if (!consent()) return null
    if (!identity) {
      try {
        identity = JSON.parse(storage?.getItem(`${key}:identity`) || 'null')
      } catch {
        /* Start a new anonymous identity. */
      }
    }
    if (
      identity &&
      (!['anonymous', 'session', 'journey'].every(
        (key) => typeof identity[key] === 'string' && /^[\w-]{1,128}$/.test(identity[key]),
      ) ||
        !Number.isFinite(identity.last))
    )
      identity = null
    if (!identity)
      identity = {
        anonymous: crypto.randomUUID(),
        session: crypto.randomUUID(),
        last: Date.now(),
        journey: crypto.randomUUID(),
      }
    if (Date.now() - identity.last > 30 * 60_000) {
      delete identity.checkoutIntent
      identity.session = crypto.randomUUID()
      identity.last = Date.now()
    }
    if (!attributed) {
      if (!identity.attribution || external || Object.values(acquisition).some(Boolean))
        identity.attribution = page
      attributed = true
    }
    return identity
  }
  const context = (extra = {}) => {
    const id = readIdentity()
    return {
      journey_id: id?.journey ?? null,
      anonymous_id: id?.anonymous ?? null,
      user_id: null,
      session_id: id?.session ?? null,
      page_view_id: page,
      configuration_id: null,
      config_version: null,
      action_id: null,
      request_id: null,
      page_name: location.pathname === '/' ? 'home' : location.pathname.split('/')[1] || 'page',
      page_version: 'analytics-v1',
      device_type: innerWidth < 750 ? 'mobile' : 'desktop',
      locale: document.documentElement.lang || null,
      currency: window.Shopify?.currency?.active || null,
      attribution_id: id?.attribution ?? null,
      analytics_consent: consent() ? 'granted' : 'unknown',
      ...extra,
    }
  }
  const saveIdentity = () => {
    try {
      if (identity && consent()) storage?.setItem(`${key}:identity`, JSON.stringify(identity))
    } catch {
      /* No storage consent workaround. */
    }
  }
  const emit = (name, properties = {}, extra = {}) => {
    if (!consent()) return
    const now = new Date().toISOString()
    queue.add({
      event_name: name,
      event_version: 1,
      event_id: crypto.randomUUID(),
      occurred_at: now,
      sent_at: now,
      event_source: 'web',
      environment,
      site_id: 'unicus_overseas',
      context: context(extra),
      properties,
      correction_of_event_id: null,
    })
    if (identity) identity.last = Date.now()
    saveIdentity()
    if (queue.pending.length >= 10) void queue.flush()
  }
  const action = () => {
    const id = readIdentity()
    if (id) {
      id.last = Date.now()
      saveIdentity()
    }
    return crypto.randomUUID()
  }
  const expose = (element, key, name, properties, extra = {}, visibleWhen) => {
    if (!element || !consent()) return
    const old = exposures.get(key)
    if (old?.done || old?.element === element) return
    old?.stop()
    const entry = { element, done: false, stop: null }
    entry.stop = observeExposure(
      element,
      () => {
        entry.done = true
        emit(name, properties, extra)
      },
      { visibleWhen },
    )
    exposures.set(key, entry)
  }
  const navTabs = { personalization: 'personalization', 'brick worlds': 'brick-worlds', accessories: 'accessories', pets: 'pets', collectables: 'collectables', creations: 'creations' }
  let pageSent = false
  const pageView = () => {
    if (!pageSent && consent() && document.visibilityState === 'visible') {
      pageSent = true
      let host = null
      try {
        host = document.referrer ? new URL(document.referrer).host : null
      } catch {
        /* No usable referrer. */
      }
      const activeNav = [...document.querySelectorAll('header nav a, header-drawer a, .menu-drawer a')].find(link => {
        try { return new URL(link.href).pathname === location.pathname && navTabs[link.textContent.trim().toLowerCase()] } catch { return false }
      })
      emit('page_view', {
        navigation_tab: activeNav ? navTabs[activeNav.textContent.trim().toLowerCase()] : null,
        route_path: location.pathname,
        referrer_host: host,
        entry_point: ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].some(name => parameters.has(name)) || acquisition.paid_search_click
          ? 'campaign'
          : external
            ? 'referral'
            : 'direct',
        ...acquisition,
      })
      if (/\/products\/[^/]+/.test(location.pathname)) emit('product_view', { route_path: location.pathname }, { action_id: action() })
      document.querySelectorAll('.shopify-section').forEach((el) =>
        expose(el, `module:${el.id}`, 'module_view', {
          module_name: el.id.slice(0, 128),
          exposure_ms: 1000,
          visible_ratio: 0.5,
        }),
      )
    }
  }
  const checkoutIntent = (path) => {
    if (!consent() || !['direct', 'cart'].includes(path)) return
    const id = readIdentity()
    id.checkoutIntent = { path, at: Date.now(), session: id.session }
    id.last = Date.now()
    saveIdentity()
  }
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('[name="checkout"], a[href="/checkout"], a[href$="/checkout"]')) checkoutIntent('cart')
  }, true)
  document.addEventListener('submit', (event) => {
    if (event.submitter?.name === 'checkout') checkoutIntent('cart')
  }, true)
  document.addEventListener('unicus:storefront-cart-added', (event) => {
    const count = Number(event.detail?.quantity)
    if (Number.isInteger(count) && count > 0 && count <= 10000) emit('storefront_cart_add', { item_count: count }, { action_id: action() })
  })
  window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
    if (!error) pageView()
  })
  document.addEventListener('visitorConsentCollected', () => {
    if (!consent()) {
      queue.clear()
      identity = null
      pageSent = false
      attributed = false
      try {
        storage?.removeItem(`${key}:identity`)
      } catch {
        /* Storage may be unavailable. */
      }
      for (const entry of exposures.values()) entry.stop()
      exposures.clear()
    } else pageView()
  })
  document.addEventListener('visibilitychange', () => {
    pageView()
    if (document.visibilityState === 'hidden') void queue.flush()
  })
  window.addEventListener('pagehide', () => void queue.flush())
  setInterval(() => void queue.flush(), 5000)
  pageView()
  return {
    emit,
    action,
    context,
    expose,
    queue,
    consent,
    checkoutIntent,
    restoreJourney(id) {
      if (consent() && id) {
        readIdentity().journey = id
        saveIdentity()
      }
    },
  }
}

export function getTracker(base, environment) {
  if (!window.unicusAnalytics)
    window.unicusAnalytics = createTracker(
      `${new URL(base).origin}/api/v1/analytics/events/batch`,
      environment,
    )
  return window.unicusAnalytics
}

if (typeof document !== 'undefined') {
  const config = document.querySelector('[data-unicus-analytics-endpoint]')
  if (config?.dataset.unicusAnalyticsEndpoint)
    getTracker(config.dataset.unicusAnalyticsEndpoint, config.dataset.environment)
}
